import {
  GraphFilterTimeRangeOptions,
  GraphFilterViewTypeOptions,
} from './graph-filter.enum';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  addDays,
  endOfDay,
  format,
  startOfDay,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { GraphFilterEvent } from '../../insights-data.interface';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { ReportParams } from '../../../view-bip-case/report-manager/report-manager.interface';

@Component({
  selector: 'app-graph-filter',
  standalone: true,
  imports: [
    NzFormModule,
    NzSegmentedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    NzSelectModule,
    NzCheckboxModule,
  ],
  templateUrl: './graph-filter.component.html',
  styleUrls: ['./graph-filter.component.scss'],
})
export class GraphFilterComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() graphFilterData!: ReportParams;
  @Output() filterChange = new EventEmitter<GraphFilterEvent>();

  private readonly destroy$ = new Subject<void>();

  timeRangeOptions = GraphFilterTimeRangeOptions;
  viewTypeOptions = GraphFilterViewTypeOptions;
  enabledViewTypeOptions = GraphFilterViewTypeOptions;
  updatingDateRangeProgrammatically = false;
  filterForm!: FormGroup;

  dateFormat = 'MMM d, yyyy';
  DATE_FORMAT = 'yyyy-MM-dd';

  startOfWeekMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
  endOfWeekFriday = addDays(this.startOfWeekMonday, 4);

  private tooltipMap = [
    '1 Day',
    'Last 7 Days',
    'Last 30 Days',
    'Last 180 Days',
    'Last 365 Days',
  ];

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef
  ) {
    const today = new Date();
    const sevenDaysAgo = addDays(today, -6);

    this.filterForm = this.fb.group({
      timeOption: [1],
      filterDateRange: [[sevenDaysAgo, today], Validators.required],
      viewType: [1],
      isTrendLineEnabled: [false],
    });
  }

  // Derived property instead of manual toggling
  get isCustomDateSelected(): boolean {
    return this.filterForm.get('timeOption')?.value === null;
  }

  ngAfterViewInit(): void {
    this.filterForm.get('timeOption')?.setValue(1);
    setTimeout(() => {
      const items = document.querySelectorAll(
        '#time-segment .ant-segmented-item'
      );
      items.forEach((el, index) => {
        el.setAttribute('title', this.tooltipMap[index]);
      });
    });
  }

  ngOnInit(): void {
    // Emit initial values
    const dateRange = this.filterForm.value.filterDateRange;
    if (this.isValidDateRange(dateRange)) {
      this.updateViewTypeOptionsByDateRange(dateRange);
      this.emitFilterChange(dateRange, this.filterForm.value.timeOption);
    }

    // Subscribe to filterDateRange changes
    this.filterForm
      .get('filterDateRange')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((dates: (Date | string | null)[]) => {
        if (!this.isValidDateRange(dates)) return;

        if (!this.updatingDateRangeProgrammatically) {
          this.filterForm
            .get('timeOption')
            ?.setValue(null, { emitEvent: false });
        }

        this.updateViewTypeOptionsByDateRange(dates);
        this.emitFilterChange(dates, null);
      });

    // Subscribe to viewType changes
    this.filterForm
      .get('viewType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(viewType => {
        this.filterForm.patchValue({ viewType }, { emitEvent: false });
        const dates = this.filterForm.value.filterDateRange;
        if (!this.isValidDateRange(dates)) return;
        this.emitFilterChange(dates, this.filterForm.value.timeOption);
      });

    // Subscribe to isTrendLineEnabled changes
    this.filterForm
      .get('isTrendLineEnabled')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(isTrendLineEnabled => {
        this.filterForm.patchValue(
          { isTrendLineEnabled },
          { emitEvent: false }
        );
        const dates = this.filterForm.value.filterDateRange;
        if (!this.isValidDateRange(dates)) return;
        this.emitFilterChange(dates, this.filterForm.value.timeOption);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphFilterData'] && changes['graphFilterData'].currentValue) {
      this.setFormData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFormData(): void {
    if (!this.graphFilterData || this.graphFilterData.id === 0) return;

    const {
      startDate,
      endDate,
      viewType,
      isTrendLineEnabled,
      selectedTimeOption,
    } = this.graphFilterData;
    const parsedStart = new Date(startDate);
    const parsedEnd = new Date(endDate);

    const current = this.filterForm.value;
    const noChange =
      current.viewType === viewType &&
      current.isTrendLineEnabled === isTrendLineEnabled &&
      current.timeOption === selectedTimeOption &&
      current.filterDateRange?.[0]?.toString() === parsedStart.toString() &&
      current.filterDateRange?.[1]?.toString() === parsedEnd.toString();

    if (noChange) return;

    this.filterForm.patchValue(
      {
        filterDateRange: [parsedStart, parsedEnd],
        viewType,
        isTrendLineEnabled,
        timeOption: selectedTimeOption ?? null,
      },
      { emitEvent: false }
    );

    this.updateViewTypeOptionsByDateRange([parsedStart, parsedEnd]);
  }

  isValidDateRange(dateRange: (Date | string | null)[]): boolean {
    return (
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      (dateRange[0] instanceof Date || typeof dateRange[0] === 'string') &&
      (dateRange[1] instanceof Date || typeof dateRange[1] === 'string')
    );
  }

  updateViewTypeOptionsByDateRange(dateRange: (Date | string | null)[]): void {
    const msInDay = 1000 * 60 * 60 * 24;
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.viewTypeOptions.forEach(opt => (opt.isEnabled = true));
      return;
    }

    const start =
      typeof dateRange[0] === 'string' ? new Date(dateRange[0]) : dateRange[0];
    const end =
      typeof dateRange[1] === 'string' ? new Date(dateRange[1]) : dateRange[1];
    const diffDays = Math.floor((end.getTime() - start.getTime()) / msInDay);

    this.viewTypeOptions.forEach(opt => {
      switch (opt.value) {
        case 1:
          opt.isEnabled = true;
          break;
        case 2:
          opt.isEnabled = diffDays >= 7;
          break;
        case 3:
          opt.isEnabled = diffDays >= 60;
          break;
        case 4:
          opt.isEnabled = diffDays > 180;
          break;
        case 5:
          opt.isEnabled = diffDays > 365;
          break;
        default:
          opt.isEnabled = true;
      }
    });

    this.enabledViewTypeOptions = this.viewTypeOptions.filter(
      opt => opt.isEnabled
    );

    // Ensure viewType is valid
    this.filterForm.patchValue(
      { viewType: this.enabledViewTypeOptions[0].value },
      { emitEvent: false }
    );
  }

  onTimeRangeChange(value: number): void {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (value) {
      case 0:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case 1:
        startDate = startOfDay(addDays(today, -6));
        endDate = endOfDay(today);
        break;
      case 2:
        startDate = startOfDay(addDays(today, -29));
        endDate = endOfDay(today);
        break;
      case 3:
        startDate = startOfDay(subMonths(today, 6));
        endDate = endOfDay(today);
        break;
      case 4:
        startDate = startOfDay(addDays(today, -364));
        endDate = endOfDay(today);
        break;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
    }
    this.updatingDateRangeProgrammatically = true;
    this.filterForm.patchValue(
      {
        timeOption: value,
        filterDateRange: [startDate, endDate],
      },
      { emitEvent: true }
    );
    this.updatingDateRangeProgrammatically = false;
    this.updateViewTypeOptionsByDateRange([startDate, endDate]);
  }

  handleViewType(): void {
    const dateRange = this.filterForm.value.filterDateRange;
    this.updateViewTypeOptionsByDateRange(dateRange);
    this.cd.markForCheck();
  }

  private emitFilterChange(
    dates: (Date | string | null)[],
    timeOption: number | null
  ): void {
    const startDate =
      typeof dates[0] === 'string' ? new Date(dates[0]) : dates[0];
    const endDate =
      typeof dates[1] === 'string' ? new Date(dates[1]) : dates[1];

    if (!startDate || !endDate) return;

    this.filterChange.emit({
      startDate: format(startDate, this.DATE_FORMAT),
      endDate: format(endDate, this.DATE_FORMAT),
      viewType: this.filterForm.value.viewType,
      isTrendLineEnabled: this.filterForm.value.isTrendLineEnabled,
      selectedTimeOption: timeOption ?? null,
    });
  }
}
